import {
  Badge,
  Box,
  Card,
  Flex,
  Grid,
  HStack,
  Separator,
  Status,
  Text,
  VStack,
} from '@chakra-ui/react'
import type { Invoice } from './invoice.types'
import {
  formatDateDE,
  formatEuro,
  invoiceDeliveryStatusPill,
  invoiceMainStatusPill,
  invoiceScopeLabel,
  periodText,
  resolveInvoiceCardTitle,
} from '../../helper/invoice.helpers'

interface InvoiceCardProps {
  invoice: Invoice
  isActive?: boolean
  onClick?: () => void
  unionEntities: { id: number; name: string }[]
  variant?: 'list' | 'header'
}

export default function InvoiceCard({
  invoice,
  isActive,
  onClick,
  unionEntities,
  variant,
}: InvoiceCardProps) {
  const entityTitle = resolveInvoiceCardTitle(invoice, unionEntities)
  const scopeLabel = invoiceScopeLabel(invoice.scope ?? invoice.type)
  const mainPill = invoiceMainStatusPill(invoice)
  const deliveryPill = invoiceDeliveryStatusPill(invoice)

  if (variant === 'header') {
    const tag = invoiceScopeLabel(invoice.scope ?? invoice.type)
    const headerTitle = resolveInvoiceCardTitle(invoice, unionEntities)

    return (
      <Card.Root variant="subtle">
        <Card.Body>
          <Flex justify="space-between" align="flex-start" gap={4}>
            <Card.Title>{invoice.invoiceNumber}</Card.Title>

            <Card.Title textDecoration={invoice.status === 'CANCELED' ? 'line-through' : 'none'}>
              {formatEuro(invoice.totalCents)}
            </Card.Title>
          </Flex>

          <Flex justify="space-between" align="center" mt={2} gap={4}>
            <HStack gap={3} minW={0}>
              {tag && (
                <Badge size="sm" variant="solid">
                  {tag}
                </Badge>
              )}

              <Card.Description lineClamp={1}>{headerTitle}</Card.Description>
            </HStack>
            <VStack align="end" gap={2} flexShrink={0}>
              <Badge size="sm" colorPalette={mainPill.colorPalette} variant="solid">
                {mainPill.label}
              </Badge>

              {deliveryPill && (
                <Badge size="sm" colorPalette={deliveryPill.colorPalette} variant="subtle">
                  {deliveryPill.label}
                </Badge>
              )}
            </VStack>
          </Flex>

          <Grid
            templateColumns={{ base: '1fr', md: invoice.scope === 'ACTIVITY' ? '1fr 1fr' : '1fr' }}
            gap={{ base: 3, md: 8 }}
            mt={4}
          >
            <Box>
              <Card.Description>Ausstellungsdatum</Card.Description>
              <Text>{formatDateDE(invoice.invoiceDate)}</Text>
            </Box>

            {invoice.scope === 'ACTIVITY' && (
              <Box>
                <Card.Description>Leistungszeitraum</Card.Description>
                <Text>{periodText(invoice.periodFrom, invoice.periodTo)}</Text>
              </Box>
            )}
          </Grid>
        </Card.Body>
      </Card.Root>
    )
  }

  return (
    <Card.Root variant={isActive ? 'subtle' : 'outline'} cursor="pointer" onClick={onClick}>
      <Card.Body>
        <Flex justify="space-between" align="flex-start">
          <VStack align="stretch" gap={5} flex="1" minW={0}>
            <VStack align="stretch" gap={2}>
              <Card.Title data-testid={`invoice-number-${invoice.id}`}>
                {invoice.invoiceNumber}
              </Card.Title>

              <HStack gap={3} wrap="wrap" color="fg.muted">
                <Status.Root
                  size="sm"
                  colorPalette={invoice.scope === 'ORDER' ? 'orange' : 'blue'}
                >
                  <Status.Indicator />
                  {scopeLabel}
                </Status.Root>

                <Separator orientation="vertical" />

                <Card.Description lineClamp={1}>{entityTitle}</Card.Description>
              </HStack>
            </VStack>

            <HStack gap={{ base: 6, md: 12 }} align="start" wrap="wrap">
              <VStack align="stretch" gap={1}>
                <Card.Description>Ausstellungsdatum</Card.Description>
                <Text>
                  {formatDateDE(invoice.sentAt ?? invoice.finalizedAt ?? invoice.createdAt)}
                </Text>
              </VStack>

              <VStack align="stretch" gap={1}>
                <Card.Description>Leistungszeitraum</Card.Description>
                <Text>{periodText(invoice.periodFrom, invoice.periodTo)}</Text>
              </VStack>
            </HStack>
          </VStack>

          <VStack align="end" gap={4} minW={{ base: '120px', md: '200px' }}>
            <Card.Title
              textAlign="right"
              textDecoration={invoice.status === 'CANCELED' ? 'line-through' : 'none'}
            >
              {formatEuro(invoice.totalCents)}
            </Card.Title>

            <VStack align="end" gap={2}>
              <Badge size="sm" colorPalette={mainPill.colorPalette} variant="solid">
                {mainPill.label}
              </Badge>

              {deliveryPill && (
                <Badge size="sm" colorPalette={deliveryPill.colorPalette} variant="subtle">
                  {deliveryPill.label}
                </Badge>
              )}
            </VStack>
          </VStack>
        </Flex>
      </Card.Body>
    </Card.Root>
  )
}
